<template>
    <div class="wrapper w-100 d-flex justify-center align-center pa-4">
        <v-card class="pa-6 text-center" max-width="460" rounded="lg" elevation="3">
            <v-icon size="56" color="primary" class="mb-3">mdi-account-multiple-outline</v-icon>
            <h2 class="text-h6 font-weight-bold mb-1">
                {{ waitingOnAGrownUp ? "You're almost in!" : 'Join a circle to get started' }}
            </h2>

            <!-- Invitations always win: an adult already chose this kid by name. -->
            <template v-if="invites.length">
                <p class="text-body-2 text-medium-emphasis mb-3">
                    Someone in your family invited you. Tap Accept to join.
                </p>
                <v-divider class="mb-2"/>
                <v-list lines="two" density="comfortable">
                    <v-list-item v-for="inv in invites" :key="inv.id" class="px-0">
                        <template #prepend>
                            <v-icon color="primary" class="me-2">mdi-account-multiple-plus</v-icon>
                        </template>
                        <v-list-item-title class="font-weight-medium">{{ inv.company_name }}</v-list-item-title>
                        <v-list-item-subtitle>Invited by {{ inv.invited_by || 'a family member' }}</v-list-item-subtitle>
                        <template #append>
                            <v-btn size="small" color="success" variant="flat"
                                   :loading="respondingId === inv.id" @click="accept(inv)">Accept</v-btn>
                        </template>
                    </v-list-item>
                </v-list>
                <v-divider class="my-2"/>
            </template>

            <!-- State A: waiting on a pending request (circle name is the hero) -->
            <template v-if="isPending">
                <p class="text-caption text-medium-emphasis mb-0 mt-2">You asked to join</p>
                <p class="text-h6 font-weight-bold mb-1">{{ pendingReq.company_name || 'a family circle' }}</p>
                <p class="text-caption text-medium-emphasis mb-2">{{ askedAgo }}</p>
                <v-sheet color="primary" rounded="lg" class="pa-3 mb-3">
                    <v-icon size="20" class="mb-1">mdi-clock-outline</v-icon>
                    <p class="text-body-2 mb-0">
                        {{ waitingOnAGrownUp
                            ? 'Waiting for a grown-up in this family to say yes.'
                            : 'Waiting for someone in this circle to say yes.' }}
                    </p>
                </v-sheet>

                <!-- Cooldown: friendly countdown instead of a hard error -->
                <template v-if="cooldownRemaining > 0">
                    <p class="text-body-2 font-weight-medium mb-0">You can try a different code in {{ cooldownText }}</p>
                    <p class="text-caption text-medium-emphasis mb-0">Give your family a moment to see your request.</p>
                </template>
                <v-btn v-else variant="text" size="small" color="primary" class="mt-1"
                       @click="openJoinDialog">Not the right family?</v-btn>
            </template>

            <!-- State: a parent declined the last request -->
            <template v-else-if="isRejected">
                <v-sheet color="grey-lighten-3" rounded="lg" class="pa-3 my-3 text-grey-darken-3">
                    <p class="text-body-2 mb-0">
                        {{ pendingReq.company_name || 'That family' }} didn't add you this time.
                        You can try a different code, or ask them to invite you.
                    </p>
                </v-sheet>
                <v-btn color="primary" variant="flat" prepend-icon="mdi-key-outline" @click="openJoinDialog">
                    Enter a circle code
                </v-btn>
            </template>

            <!-- State B: in limbo, nothing pending yet -->
            <template v-else>
                <p v-if="waitingOnAGrownUp" class="text-body-2 text-medium-emphasis mb-3 mt-2">
                    Your account is ready. To start, someone in your family needs to add you, or you
                    can enter your family's circle code.
                </p>
                <p v-else class="text-body-2 text-medium-emphasis mb-3 mt-2">
                    You're not in a circle yet. Enter a circle code to join one, or ask someone in
                    the circle to invite you.
                </p>
                <v-btn color="primary" variant="flat" prepend-icon="mdi-key-outline" @click="openJoinDialog">
                    Enter a circle code
                </v-btn>
            </template>

            <!-- Reassurance: what to tell the parent, and how they'll recognise you -->
            <p v-if="firstName" class="text-caption text-medium-emphasis mt-4 mb-0">
                They'll see you as <strong>{{ firstName }}</strong>.
            </p>
            <p v-if="isPending && waitedLong" class="text-caption text-medium-emphasis mt-1 mb-0">
                Still waiting?
                {{ waitingOnAGrownUp ? 'Ask a grown-up to' : 'Ask them to' }}
                open SimpliGPS Go, tap their circle, and approve you.
            </p>

            <div class="d-flex justify-center ga-2 mt-4">
                <v-btn variant="tonal" color="primary" prepend-icon="mdi-refresh"
                       :loading="checking" @click="check">Check again</v-btn>
                <v-btn variant="text" @click="logout">Logout</v-btn>
            </div>
        </v-card>

        <!-- Join-a-different-family dialog (live name lookup + confirm) -->
        <v-dialog v-model="showDialog" max-width="400">
            <v-card rounded="lg" class="pa-2">
                <v-card-title class="d-flex justify-space-between align-center">
                    <span class="text-h6">
                        {{ waitingOnAGrownUp ? 'Join a different family' : 'Join a circle' }}
                    </span>
                    <v-btn icon="mdi-close" size="small" variant="text" @click="closeDialog"/>
                </v-card-title>
                <v-card-text>
                    <p class="text-body-2 text-medium-emphasis mb-3">
                        {{ waitingOnAGrownUp
                            ? 'Ask a grown-up in your family for their circle code.'
                            : 'Ask someone in the circle for their circle code.' }}
                        It looks like XXX-XXX.
                    </p>
                    <div class="d-flex align-center justify-center ga-2 mb-2">
                        <v-text-field v-model="part1" maxlength="3" hide-details density="comfortable"
                                      variant="outlined" class="code-segment"
                                      @update:model-value="(v) => onSegment(1, v)"/>
                        <span class="text-h5 font-weight-bold">-</span>
                        <v-text-field v-model="part2" maxlength="3" hide-details density="comfortable"
                                      variant="outlined" class="code-segment"
                                      @update:model-value="(v) => onSegment(2, v)"/>
                    </div>
                    <v-alert v-if="foundName" type="success" variant="tonal" density="compact" class="mb-1">
                        Found: <strong>{{ foundName }}</strong>
                    </v-alert>
                    <v-alert v-if="dialogError" type="error" variant="tonal" density="compact" class="mb-1">
                        {{ dialogError }}
                    </v-alert>
                    <v-alert v-if="foundName && isPending" type="info" variant="tonal" density="compact" class="mb-1">
                        This will cancel your request to <strong>{{ pendingReq.company_name }}</strong> and ask
                        <strong>{{ foundName }}</strong> instead. That's okay - you can only wait on one family at a time.
                    </v-alert>
                </v-card-text>
                <v-card-actions class="px-4 pb-4">
                    <v-spacer/>
                    <v-btn variant="text" @click="closeDialog">Cancel</v-btn>
                    <v-btn color="primary" :loading="joining" :disabled="!foundName" @click="sendJoin">
                        Send request
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script setup>
import {computed, onMounted, onUnmounted, ref} from "vue";
import {send} from "@/composables/apiClient";
import {useAuthStore} from "@/stores/authStore";
import {useSnackbarStore} from "@/stores/snackBarStore";
import router from "@/router";

const authStore = useAuthStore();
const {showSnackbar} = useSnackbarStore();

const invites = ref([]);
const respondingId = ref(null);
const checking = ref(false);
// 1s ticker drives the live cooldown countdown and the relative "asked" time.
const nowTick = ref(Date.now());

const pendingReq = computed(() => authStore.user?.pending_join_request ?? null);
const isPending = computed(() => pendingReq.value?.status === 'pending');
const isRejected = computed(() => pendingReq.value?.status === 'rejected');
const firstName = computed(() => authStore.user?.first_name ?? '');
// A minor waiting to be added, rather than an adult who belongs to no circle.
// Both reach this screen and both leave it the same way (a circle code), but
// telling an adult to ask a grown-up reads as a bug.
const waitingOnAGrownUp = computed(() => authStore.inCircleLimbo);
// Whether this screen still applies - the same condition the layout gates on.
//
// Load-bearing: advanceIfJoined polls every 18s and reloads the page the moment
// this goes false. Testing inCircleLimbo alone would make it false immediately
// for an adult held by circle_none, so the page would reload every 18 seconds
// and land straight back here - an endless refresh loop on the one screen that
// user cannot leave.
const stillHeld = computed(() => authStore.inCircleLimbo || authStore.hasNoCircle);

const requestedAtMs = computed(() => {
    const ts = pendingReq.value?.requested_at;
    return ts ? new Date(ts).getTime() : 0;
});
const COOLDOWN_MS = 5 * 60 * 1000;
const cooldownRemaining = computed(() =>
    isPending.value && requestedAtMs.value
        ? Math.max(0, requestedAtMs.value + COOLDOWN_MS - nowTick.value)
        : 0);
const cooldownText = computed(() => {
    const s = Math.ceil(cooldownRemaining.value / 1000);
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
});
const askedAgo = computed(() => {
    if (!requestedAtMs.value) return '';
    const mins = Math.floor((nowTick.value - requestedAtMs.value) / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `Asked ${mins} minute${mins > 1 ? 's' : ''} ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `Asked ${hrs} hour${hrs > 1 ? 's' : ''} ago`;
    return 'Asked yesterday';
});
const waitedLong = computed(() => requestedAtMs.value && (nowTick.value - requestedAtMs.value) > 10 * 60 * 1000);

const loadInvites = () => {
    send('get', 'circle/invitations')
        .then((r) => { invites.value = r.data?.data ?? []; })
        .catch(() => {});
};

const accept = (inv) => {
    respondingId.value = inv.id;
    send('post', `accept-invitation/${inv.token}`, {})
        .then(() => {
            showSnackbar(`Joined ${inv.company_name || 'the circle'}`);
            router.go();
        })
        .catch((e) => showSnackbar(e.response?.data?.message ?? 'Could not accept', 'error'))
        .finally(() => respondingId.value = null);
};

// Re-hydrate; a parent may have approved a request or added them.
const advanceIfJoined = async () => {
    await authStore.hydrateProfile();
    if (!stillHeld.value) router.go();
};
const check = async () => {
    checking.value = true;
    await advanceIfJoined();
    checking.value = false;
    if (stillHeld.value) {
        loadInvites();
        showSnackbar(
            waitingOnAGrownUp.value
                ? 'No circle yet - ask a grown-up to add you.'
                : 'No circle yet - enter a circle code or ask to be invited.',
        );
    }
};
const logout = () => authStore.logout();

// --- Join-a-different-family dialog ---
const showDialog = ref(false);
const part1 = ref("");
const part2 = ref("");
const code = computed(() => part1.value + part2.value);
const foundName = ref("");
const dialogError = ref("");
const joining = ref(false);

const openJoinDialog = () => { showDialog.value = true; };
const onSegment = (n, val) => {
    const clean = (val || "").toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 3);
    if (n === 1) part1.value = clean; else part2.value = clean;
    foundName.value = "";
    dialogError.value = "";
    if (code.value.length === 6) lookup();
};
const lookup = () => {
    send('get', `circle/lookup?code=${code.value}`)
        .then((r) => { foundName.value = r.data?.data?.company_name ?? ""; })
        .catch(() => { dialogError.value = "No family found for that code. Double-check it with your grown-up."; });
};
// Gate send on a RESOLVED name so a doomed code can't burn the 5-min cooldown.
const sendJoin = () => {
    if (!foundName.value) return;
    joining.value = true;
    dialogError.value = "";
    send('post', 'circle/join', {code: code.value})
        .then(async (r) => {
            if (r.data.success) {
                showSnackbar(`Request sent to ${foundName.value}. We'll let you know when they say yes.`);
                closeDialog();
                await advanceIfJoined();
            }
        })
        .catch((err) => {
            dialogError.value = err.response?.status === 429
                ? "Hang on a sec - you can try another family in a little while."
                : (err.response?.data?.message ?? "That didn't go through. Check your internet and try again.");
        })
        .finally(() => joining.value = false);
};
const closeDialog = () => {
    showDialog.value = false;
    part1.value = "";
    part2.value = "";
    foundName.value = "";
    dialogError.value = "";
};

let tickTimer = null;
let pollTimer = null;
onMounted(() => {
    loadInvites();
    tickTimer = setInterval(() => { nowTick.value = Date.now(); }, 1000);
    // Light poll so the screen auto-advances even if push is dropped (dev).
    pollTimer = setInterval(advanceIfJoined, 18000);
});
onUnmounted(() => {
    if (tickTimer) clearInterval(tickTimer);
    if (pollTimer) clearInterval(pollTimer);
});
</script>

<style scoped>
.wrapper {
    height: 100vh;
}

.code-segment {
    max-width: 96px;
}

.code-segment :deep(input) {
    text-align: center;
    text-transform: uppercase;
    letter-spacing: 4px;
    font-weight: 700;
}
</style>
